// <xte-illust> — 96グリッドの空状態イラストをスプライトから描画するWeb Component。
//
// 出典: 15_空状態と読み込みとエラーの視覚設計.md 2.4節・2.5節・11.1節、
// IMPLEMENTATION.md 5.2節（Web Components規約）。
//
// <xte-icon>（core/icon.js）とは異なり、参照するスプライトを常時インライン
// 展開しない。イラストは空状態・エラー画面でしか使われず、1画面のセッション
// の大半では一度も表示されないため、初回に実際へ描画されるタイミングで
// 専用スプライト dist/icons/xte-illustrations.svg を遅延フェッチする
// （15章2.5節の決定。アイコンスプライートとは分離した別ファイル）。

const SPRITE_URL = '/wp-content/themes/xte-theme/dist/icons/xte-illustrations.svg';

// モジュールスコープでフェッチ結果をキャッシュする（15章2.5節: 同一セッション内の
// 再フェッチを防ぐ）。複数の <xte-illust> がほぼ同時に接続されても、
// Promiseの参照を同期的に共有するため実際のfetchは1回しか発生しない。
let spriteLoadPromise = null;

async function ensureSpriteLoaded() {
	if (spriteLoadPromise) return spriteLoadPromise;

	spriteLoadPromise = (async () => {
		const res = await fetch(SPRITE_URL, { credentials: 'omit' });
		if (!res.ok) {
			throw new Error(`[xte-illust] sprite fetch failed: ${res.status}`);
		}
		const svgText = await res.text();

		const holder = document.createElement('div');
		holder.hidden = true;
		holder.setAttribute('aria-hidden', 'true');
		holder.innerHTML = svgText;
		document.body.append(holder);
	})().catch((err) => {
		// 失敗時は次回の接続時に再フェッチできるようキャッシュをリセットする。
		// ここで例外を握りつぶすのは、装飾用イラストの取得失敗でページ全体を
		// 壊さないため（15章10.1節: イラストは装飾。意味の伝達は文言側が担う）。
		spriteLoadPromise = null;
		console.warn('[xte-illust] スプライトの読み込みに失敗しました', err);
	});

	return spriteLoadPromise;
}

// 重大度→CSS修飾子の対応（15章2.3節の重大度マッピング）。
// 'normal'（既定・省略可）はクラスを付けず、.xte-illust の既定色をそのまま使う。
const SEVERITY_CLASSES = new Set(['warning', 'danger']);

// 未定義名のフォールバック（11_SVGアイコンシステム_基盤.md 4.5節と同じ方針:
// 無音で消えるより「異常が見える」方が事故に気づきやすい）。
// video-empty は「入れ物はあるが中身が無い」という最も汎用的な空状態の概念で
// あり、未知のイラスト名を指定された場合の代替として最も違和感が少ない。
const FALLBACK_ILLUST_NAME = 'video-empty';

export class XteIllust extends HTMLElement {
	static observedAttributes = ['name', 'size', 'severity'];

	#controller = null;
	#ready = false; // スプライト注入が完了し、初回描画が済んだかどうか

	connectedCallback() {
		this.#controller = new AbortController();
		this.#load();
	}

	disconnectedCallback() {
		// スプライトのfetch自体はモジュールスコープで共有されるため中断しないが、
		// このインスタンスがフェッチ待機中に切断された場合、待機完了後に
		// 切断済みの要素へ描画してしまわないよう signal で判定する
		// （IMPLEMENTATION.md 5.2: disconnectedCallbackでのクリーンアップ）。
		this.#controller?.abort();
		this.#controller = null;
	}

	attributeChangedCallback() {
		// 初回描画がまだなら #load() 側の描画に任せる（スプライト未注入の状態で
		// #render() を呼ぶと symbol 不在と誤判定してしまうため）。
		if (this.isConnected && this.#ready) this.#render();
	}

	async #load() {
		const controller = this.#controller;
		await ensureSpriteLoaded();
		if (controller.signal.aborted) return;
		this.#ready = true;
		this.#render();
	}

	#symbolExists(name) {
		return Boolean(document.getElementById(`xte-illust-${name}`));
	}

	#render() {
		const requestedName = this.getAttribute('name');
		const name = requestedName && this.#symbolExists(requestedName) ? requestedName : null;

		if (!name) {
			console.warn(
				`[xte-illust] name="${requestedName ?? ''}" はスプライトに存在しません。` +
					`フォールバックとして "${FALLBACK_ILLUST_NAME}" を表示します。`
			);
		}

		const resolvedName = name || FALLBACK_ILLUST_NAME;
		const size = this.getAttribute('size') || '96';
		const severity = this.getAttribute('severity');
		const severityClass = SEVERITY_CLASSES.has(severity) ? `xte-illust--${severity}` : '';

		this.className = ['xte-illust', severityClass].filter(Boolean).join(' ');

		// 装飾のみ（15章10.1節）。意味の伝達は常に文言（見出し・説明）側が担うため、
		// aria-hidden="true" + focusable="false" を固定で付与し、単独のaria-labelは
		// 付与しない（重複読み上げの防止）。
		this.innerHTML =
			`<svg width="${size}" height="${size}" viewBox="0 0 96 96" aria-hidden="true" focusable="false">` +
			`<use href="#xte-illust-${resolvedName}"></use>` +
			`</svg>`;

		// 遅延フェッチ完了直後にいきなり表示すると「チカッ」というちらつきに
		// なりやすいため、opacityのみのフェードイン（illustration.cssの
		// .xte-illust--visible）で緩和する。同期的にクラスを足すと直前の
		// opacity:0状態がレンダリングされずtransitionが発火しないため、
		// 1フレーム後に付与する。
		requestAnimationFrame(() => {
			this.classList.add('xte-illust--visible');
		});
	}
}

if (!customElements.get('xte-illust')) {
	customElements.define('xte-illust', XteIllust);
}
